'use client';

import { useCallback, useEffect, useRef } from 'react';

import { usePathname } from 'next/navigation';

import {
  clearEnrollmentTracking,
  findEnrollmentPackItemsInCart,
  isEnrollmentFlowRoute,
} from '@/helpers/utils/enrollmentFlowTracker';
import { useGetShoppingCartQuery, useRemoveListFromCartMutation } from '@/store/api/shoppingCart';

const PREVIOUS_PATH_KEY = 'enrollment_previous_path';

const EnrollmentFlowTracker = () => {
  const pathname = usePathname();
  const isRemovingRef = useRef(false);

  const { data: cartData } = useGetShoppingCartQuery(undefined);
  const cart = cartData?.data?.cart;
  const [removeListFromCart] = useRemoveListFromCartMutation();

  const getPreviousPath = (): string | null => {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(PREVIOUS_PATH_KEY);
  };

  const setPreviousPath = (path: string): void => {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(PREVIOUS_PATH_KEY, path);
  };

  const handleRemoveEnrollmentPackItems = useCallback(async () => {
    if (!cart || cart.length === 0) {
      clearEnrollmentTracking();
      return;
    }

    const packItems = findEnrollmentPackItemsInCart(cart);

    if (packItems.length > 0) {
      const itemIds = packItems.map(item => item.itemId);

      try {
        await removeListFromCart({ ids: itemIds, removeForcefully: true }).unwrap();
      } catch {
        // Error handled silently
      }
    }

    clearEnrollmentTracking();
  }, [cart, removeListFromCart]);

  useEffect(() => {
    const currentPath = pathname;
    const previousPath = getPreviousPath();

    if (previousPath === null) {
      setPreviousPath(currentPath);
      return;
    }

    const wasOnEnrollmentRoute = isEnrollmentFlowRoute(previousPath);
    const isOnEnrollmentRoute = isEnrollmentFlowRoute(currentPath);

    if (wasOnEnrollmentRoute && !isOnEnrollmentRoute && !isRemovingRef.current) {
      isRemovingRef.current = true;

      handleRemoveEnrollmentPackItems().finally(() => {
        isRemovingRef.current = false;
      });
    }

    setPreviousPath(currentPath);
  }, [pathname, handleRemoveEnrollmentPackItems]);

  return null;
};

export default EnrollmentFlowTracker;
